import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import {
  useInvalidatePaymentsManagement,
  usePaymentsManagementQuery,
} from '@/api/query/payment';
import type { PaymentManagementPublic, PaymentManagementResponse } from '@/api/type/payment';
import { useAppContext } from '@/contexts/AppContext';
import type { Payment } from '@/types';

interface PaymentContextType {
  payments: Payment[];
  management: PaymentManagementResponse | null;
  isManagementLoading: boolean;
  isManagementFetching: boolean;
  managementError: string | null;
  addPayment: (payment: Payment) => void;
  getPaymentsByMemberId: (memberId: string) => Payment[];
  getManagementByMemberId: (memberId: string) => PaymentManagementPublic | undefined;
  refreshManagement: () => Promise<void>;
}

const PaymentContext = createContext<PaymentContextType | undefined>(undefined);

export function PaymentProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAppContext();
  const [payments, setPayments] = useState<Payment[]>([]);
  const invalidateManagement = useInvalidatePaymentsManagement();

  const { data, isLoading, isFetching, isError, error } =
    usePaymentsManagementQuery(isAuthenticated);

  useEffect(() => {
    if (!isAuthenticated) {
      setPayments([]);
    }
  }, [isAuthenticated]);

  const management = isAuthenticated ? (data ?? null) : null;

  const refreshManagement = useCallback(async () => {
    await invalidateManagement();
  }, [invalidateManagement]);

  const addPayment = useCallback((payment: Payment) => {
    setPayments((prev) => [payment, ...prev.filter((item) => item.id !== payment.id)]);
  }, []);

  const getPaymentsByMemberId = useCallback(
    (memberId: string) => payments.filter((payment) => payment.memberId === memberId),
    [payments]
  );

  const getManagementByMemberId = useCallback(
    (memberId: string) =>
      management?.data.find((item) => String(item.user_id) === memberId),
    [management]
  );

  const contextValue: PaymentContextType = {
    payments,
    management,
    isManagementLoading: isAuthenticated && isLoading,
    isManagementFetching: isAuthenticated && isFetching,
    managementError: isError
      ? error instanceof Error
        ? error.message
        : 'Unable to load payment management. Please try again.'
      : null,
    addPayment,
    getPaymentsByMemberId,
    getManagementByMemberId,
    refreshManagement,
  };

  return <PaymentContext.Provider value={contextValue}>{children}</PaymentContext.Provider>;
}

export function usePaymentContext() {
  const context = useContext(PaymentContext);
  if (context === undefined) {
    throw new Error('usePaymentContext must be used within a PaymentProvider');
  }
  return context;
}
